import { useState, useEffect, useCallback, useContext } from 'react'
import { useSelector, useDispatch, shallowEqual } from 'react-redux'
import classNames from 'classnames'
import { useLocation, useParams } from 'react-router-dom'
import { createUseStyles } from 'react-jss'
import queryString from 'query-string'
import { Context as GeneralContext } from '@/context'
import { Context as ScrollbarContext } from '@/context/scrollbar'
import LocomotiveScroll from '@/components/LocomotiveScroll'
import { useRaf } from '@/components/Handlers'
import Meta from '@/components/Meta'
import Hero from '@/components/Hero'
import StoreLocatorMap from '@/components/StoreLocatorMap'
import StoreLocatorFilter from '@/components/StoreLocatorFilter'
import actions from '@/actions'
import style from './style'

const useStyles = createUseStyles(style)

const StoreFinder = ({ stub }) => {
  const { setPageAnimationReady, headerHeight } = useContext(GeneralContext)
  const { scroll, limit } = useContext(ScrollbarContext)
  const classes = useStyles({ headerHeight })
  const [load, setLoad] = useState(false)
  const [isStickyVisible, setStickyVisible] = useState(true)
  const { search } = useLocation()
  const params = useParams()

  /*------------------------------
  Redux Connect
  ------------------------------*/
  const { current, page, stores, strings, filters } = useSelector((state) => ({
    current: state.page.current,
    mediaQueryKey: state.mediaQuery.key,
    page: state.page.pages[stub] || {},
    strings: state.language.string,
    stores: state.stores.results,
    filters: state.stores.filters,
  }), shallowEqual)

  /*------------------------------
  Filter stores
  ------------------------------*/
  const [currentStores, setCurrentStores] = useState(stores)
  useEffect(() => {
    if (stores.length > 0) setCurrentStores(stores)
  }, [stores])

  useEffect(() => {
    if (filters) {
      const newStores = stores.filter((s) => s.category[0].term_id === filters)
      setCurrentStores(newStores)
    } else {
      setCurrentStores(stores)
    }
  }, [filters])

  /*------------------------------
  Redux Actions
  ------------------------------*/
  const dispatch = useDispatch()
  const getPage = useCallback((index) => dispatch(actions.page.getDataPage(index)), [dispatch])
  const setCurrentPage = useCallback((index) => dispatch(actions.page.setCurrent(index)), [dispatch])
  const updatePageReady = useCallback((index) => dispatch(actions.loading.setReady(index)), [dispatch])
  const setSiteLoaded = useCallback(() => dispatch(actions.loading.setSiteLoaded()), [dispatch])
  const setResultVisible = useCallback((bool) => dispatch(actions.stores.setResultVisible(bool)), [dispatch])
  const getStoresAll = useCallback(() => dispatch(actions.stores.getStoresAll()), [dispatch])
  const setModalOpen = useCallback((bool) => dispatch(actions.modal.setOpen(bool)), [dispatch])
  const setModalStoreID = useCallback((num) => dispatch(actions.modal.setModalStoreID(num)), [dispatch])

  /*------------------------------
  Store Page
  ------------------------------*/
  useEffect(() => {
    if (Object.keys(params).length > 0 && stores.length > 0) {
      const currentStore = stores.find((s) => s.slug === params.stub)
      setModalStoreID(currentStore.id)
      setModalOpen(true)
    }
  }, [stores])

  /*------------------------------
  Complete Preload
  ------------------------------*/
  const completePreload = () => {
    updatePageReady(true)
    setSiteLoaded()
    setLoad(true)
  }

  /*------------------------------
  Initialize
  ------------------------------*/
  const initialize = () => {
    if (current !== stub) setCurrentPage(stub, 'pages')
    if (stores.length === 0) getStoresAll()
    getPage(stub)
  }

  /*------------------------------
  If the page is loaded from redux, start to preload media
  ------------------------------*/
  useEffect(() => {
    if (Object.keys(page).length > 0) completePreload()
  }, [page])

  /*------------------------------
  Did Mount
  ------------------------------*/
  useEffect(() => {
    if (!load) initialize()
  }, [])

  /*------------------------------
  Activate Page Animation
  ------------------------------*/
  useEffect(() => {
    if (load) setPageAnimationReady(true)
  }, [load])

  /*------------------------------
  Render Helmet
  ------------------------------*/
  const renderHelmet = () => {
    return load && (
      <Meta
        title={page.yoast_meta.find((y) => y.property?.includes('title')).content || page.title.rendered}
        meta={page.yoast_meta}
        schema={page.yoast_json_ld}
      />
    )
  }

  /*------------------------------
  Render Cover
  ------------------------------*/
  const renderCover = () => {
    const searchTerm = search ? atob(queryString.parse(search, { decode: false }).q) : ''
    return (
      <Hero
        className={classes.hero}
        locator
        minimize
        viewportHeight
        headerHeight={headerHeight}
        searchTerm={searchTerm}
        setResultVisible={setResultVisible}
      />
    )
  }

  /*------------------------------
  Render Store Locator Map
  ------------------------------*/
  const renderStoreLocatorMap = () => {
    return (
      <div className={classes.storeLocatorMap}>
        <StoreLocatorMap stores={currentStores} />
        {/* <StoreLocatorFilter /> */}
      </div>
    )
  }

  /*------------------------------
  Manage Sticky Visibility
  ------------------------------*/
  useRaf(() => {
    const progress = scroll.current.y / limit.current.y
    if (progress > 0.8) {
      setStickyVisible(false)
    } else {
      setStickyVisible(true)
    }
  })

  /*------------------------------
  Render Sticky Phone
  ------------------------------*/
  const renderStickyPhone = () => {
    return load && (
      <div
        className={classNames({
          [classes.stickyBar]: true,
          [classes.stickyBarVisible]: isStickyVisible && stores.length > 0,
        })}
      >
        <div className={classes.stickyBarContainer}>
          <div className={classes.stickyBarWrapper}>
            <p>{strings.sticky_text}</p>
            {' '}
            <strong>{strings.sticky_number}</strong>
          </div>
        </div>
      </div>
    )
  }

  /*------------------------------
  Render Content
  ------------------------------*/
  const renderContent = () => {
    return load && (
      <>
        <div className={classes.viewport}>
          {renderCover()}
          {renderStoreLocatorMap()}
        </div>
      </>
    )
  }

  return (
    <>
      <LocomotiveScroll
        init={load}
        className={`page pageStoreFinder ${classes.root}`}
      >
        {renderHelmet()}
        {renderContent()}
      </LocomotiveScroll>
      {renderStickyPhone()}
    </>
  )
}

export default StoreFinder
